import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import ROUTES, { YEAR_QUERY_PARAM } from '../routes/routeConstants';
import {
  ScenarioControllerApi,
  LocationControllerApi,
  YearPlanControllerApi,
  TreatmentPlanTemplateControllerApi,
  PatientControllerApi,
  PatientRoomPropertyControllerApi
} from '../api/generated';
import type {
  YearPlanResponse,
  TreatmentPlanTemplateResponse,
  TreatmentPlanDto,
  TreatmentWeekDto,
  InterventionGroupDto,
  InterventionGroupTemplateResponse
} from '../api/generated';
import { createApiConfiguration } from '../api/apiConfig';
import { aggregateCapacity } from '../utils/ganttUtils';
import { setHasUnsavedChanges } from '../store/application/applicationSlice';
import { setPatients, clearPatients } from '../store/patients/patientsSlice';
import {
  setRoomProperties,
  clearRoomProperties
} from '../store/roomProperties/roomPropertiesSlice';
import type { RoomProperty } from '../store/roomProperties/roomPropertiesSlice';
import { showError } from '../store/errorDialog/errorDialogSlice';
import { useAppDispatch } from '../store/hooks';
import type { MoveHoverParams } from '../components/GanttChart/GanttCell';

async function fetchRoomProperties(): Promise<RoomProperty[]> {
  const config = createApiConfiguration();
  const roomPropertyApi = new PatientRoomPropertyControllerApi(config);
  const response = await roomPropertyApi.listPatientRoomProperties();
  return response.data.map((rp) => ({
    id: rp.id,
    name: rp.name
  }));
}

export interface ChangeSummary {
  currentTotalCapacity: number;
  serverTotalCapacity: number;
  currentTotalPlans: number;
  serverTotalPlans: number;
  treatmentPlansAdded: Record<string, number>;
  treatmentPlansDeleted: Record<string, number>;
}

// Creates a new intervention group from a template group, with fresh ids
function buildGroupFromTemplate(
  templateGroup: InterventionGroupTemplateResponse,
  capacity: number | undefined
): InterventionGroupDto {
  return {
    id: crypto.randomUUID(),
    name: templateGroup.name,
    icon: templateGroup.icon,
    order: templateGroup.order,
    barSize: templateGroup.barSize,
    reimbursementAmount: templateGroup.reimbursementAmount,
    patientType: templateGroup.patientType,
    capacity: capacity ?? 0,
    interventions: templateGroup.interventions.map((iv) => ({
      id: crypto.randomUUID(),
      interventionTemplateId: iv.id,
      name: iv.name,
      description: iv.description,
      color: iv.color,
      icon: iv.icon,
      durationMinutes: iv.durationMinutes,
      sessionType: iv.sessionType,
      mandatoryStaffCompetenceRequirements: iv.mandatoryStaffCompetenceRequirements,
      flexStaffCompetenceRequirements: iv.flexStaffCompetenceRequirements,
      allowedDays: iv.allowedDays,
      roomRequirementIds: iv.roomRequirements?.flatMap((r) => (r.id ? [r.id] : []))
    })),
    placementRules: templateGroup.placementRules
  };
}

// Creates the weeks for a new plan from a template, shifted by the given week offset
function buildWeeksFromTemplate(
  template: TreatmentPlanTemplateResponse,
  weekOffset: number,
  capacity: number | undefined
): TreatmentWeekDto[] {
  return template.weeks.map((tw) => ({
    weekNumber: tw.weekNumber + weekOffset,
    groups: tw.groups.map((tg) => buildGroupFromTemplate(tg, capacity))
  }));
}

// Returns a copy of the plan with the capacity applied to the targeted groups
function applyCapacityToPlan(
  plan: TreatmentPlanDto,
  capacity: number,
  targetGroupIds: ReadonlySet<string>
): TreatmentPlanDto {
  return {
    ...plan,
    weeks: plan.weeks.map((w) => ({
      ...w,
      groups: (w.groups ?? []).map((g) =>
        g.id && targetGroupIds.has(g.id) ? { ...g, capacity } : g
      )
    }))
  };
}

// Parses the ?year= query param value; null when absent or not a valid year
function parseYearParam(value: string | null): number | null {
  if (!value) return null;
  const year = Number(value);
  return Number.isInteger(year) && year > 0 ? year : null;
}

function hasYearPlanChanged(
  current: YearPlanResponse | null,
  server: YearPlanResponse | null
): boolean {
  if (current === server) return false;
  if (!current || !server) return current !== server;
  return JSON.stringify(current.treatmentPlans) !== JSON.stringify(server.treatmentPlans);
}

export function useYearPlan(locationId: string | undefined) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // The selected year lives in the URL (?year=) so it survives page refresh
  // and location switches. Absent/invalid param → current calendar year until
  // the initial fetch resolves the scenario year and syncs it back to the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlYear = parseYearParam(searchParams.get(YEAR_QUERY_PARAM));
  const selectedYear = urlYear ?? new Date().getFullYear();

  // Latest-value refs so the fetch effects can read the URL year and write the
  // URL without depending on them (setSearchParams changes identity on every
  // search change, which would otherwise re-trigger the fetch effects). Synced
  // in an effect declared before the fetch effects so they always see current
  // values.
  const urlYearRef = useRef(urlYear);
  const setSearchParamsRef = useRef(setSearchParams);
  useEffect(() => {
    urlYearRef.current = urlYear;
    setSearchParamsRef.current = setSearchParams;
  });

  const writeYearToUrl = useCallback((year: number, options?: { replace?: boolean }): void => {
    setSearchParamsRef.current((prev) => {
      const next = new URLSearchParams(prev);
      next.set(YEAR_QUERY_PARAM, String(year));
      return next;
    }, options);
  }, []);

  // User-picked year changes push a history entry; internal syncs (initial
  // scenario resolution, revert on fetch error) replace it instead.
  const setSelectedYear = useCallback(
    (year: number): void => {
      writeYearToUrl(year);
    },
    [writeYearToUrl]
  );

  const [yearPlan, setYearPlanLocal] = useState<YearPlanResponse | null>(null);
  const [treatmentPlanTemplates, setTreatmentPlanTemplates] = useState<
    TreatmentPlanTemplateResponse[]
  >([]);
  // Stores the last server-synced state, used to determine if local edits exist
  const serverYearPlan = useRef<YearPlanResponse | null>(null);

  // Sets state from a server response — resets the server snapshot and clears unsaved changes state
  const setYearPlanFromServer = useCallback(
    (plan: YearPlanResponse | null) => {
      setYearPlanLocal(plan);
      serverYearPlan.current = plan;
      dispatch(setHasUnsavedChanges(false));
    },
    [dispatch]
  );

  // Applies a confirmed local edit — compares against server state to track unsaved changes state
  const applyYearPlanEdit = useCallback(
    (plan: YearPlanResponse | null) => {
      setYearPlanLocal(plan);
      dispatch(setHasUnsavedChanges(hasYearPlanChanged(plan, serverYearPlan.current)));
    },
    [dispatch]
  );

  const scenarioYearRef = useRef<number>(selectedYear);
  const [scenarioId, setScenarioId] = useState<string | null>(null);
  const [locationName, setLocationName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Drop the previous location's scenario the moment the location changes, and do it during render
  // rather than from an effect.
  //
  // `scenarioId` is only ever assigned by a completed fetch, so without this it keeps pointing at
  // the location we just left for as long as the new location's scenario takes to arrive. Everything
  // downstream is keyed on the pair (locationId, scenarioId) — week statuses, the scenario lock, the
  // week calendar — so during that window they ask for a scenario that does not exist in this
  // location. The backend answers 404, and the week-status poll classifies 404 as permanent: it
  // stops polling and opens an error dialog, on every single location switch.
  //
  // An effect would be too late. Those hooks live in the same component, so their effects for the
  // render that introduced the new location have already run — and already fired the mismatched
  // request — by the time an effect here could clear this. Resetting during render re-renders before
  // any of them commit, so they see `undefined` and skip the fetch instead.
  const [renderedLocationId, setRenderedLocationId] = useState(locationId);
  if (locationId !== renderedLocationId) {
    setRenderedLocationId(locationId);
    setScenarioId(null);
  }

  const totalWeeks = yearPlan?.weeks.length ?? 52;
  // Snapshot taken before a move starts, used to restore state on discard
  const yearPlanSnapshot = useRef<YearPlanResponse | null>(null);

  // Initial fetch
  useEffect(() => {
    if (!locationId) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const config = createApiConfiguration();
        const scenarioApi = new ScenarioControllerApi(config);
        const locationApi = new LocationControllerApi(config);
        const yearPlanApi = new YearPlanControllerApi(config);

        // A year in the URL (refresh, location switch) wins over the backend's
        // notion of the current scenario.
        const initialYear = urlYearRef.current;
        const [scenarioResponse, locationsResponse] = await Promise.all([
          initialYear === null
            ? scenarioApi.getCurrentScenario({ locationId })
            : scenarioApi.getScenarioForYear({ locationId, year: initialYear }),
          locationApi.getLocations()
        ]);

        const scenarioId = scenarioResponse.data.id;
        setScenarioId(scenarioId);

        const year = scenarioResponse.data.year;
        scenarioYearRef.current = year;
        if (initialYear !== year) {
          writeYearToUrl(year, { replace: true });
        }

        if (scenarioId) {
          const patientApi = new PatientControllerApi(config);
          const [yearPlanResponse, patientsResponse, roomProps] = await Promise.all([
            yearPlanApi.getYearPlan({ locationId, scenarioId }),
            patientApi.listPatients({ locationId }),
            fetchRoomProperties()
          ]);
          setYearPlanFromServer(yearPlanResponse.data);
          dispatch(setPatients(patientsResponse.data));
          dispatch(setRoomProperties(roomProps));
        }

        const match = locationsResponse.data.find((l) => l.id === locationId);
        setLocationName(match?.name ?? null);
      } catch (error) {
        console.error('Failed to load year plan on initial fetch:', error);
        navigate(`/${ROUTES.errorPage}`);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [locationId, setYearPlanFromServer, writeYearToUrl, dispatch, navigate]);

  // Fetch on year change
  useEffect(() => {
    if (!locationId || selectedYear === scenarioYearRef.current) return;

    const fetchYearPlan = async () => {
      setLoading(true);
      try {
        const config = createApiConfiguration();
        const scenarioApi = new ScenarioControllerApi(config);
        const yearPlanApi = new YearPlanControllerApi(config);

        const scenarioResponse = await scenarioApi.getScenarioForYear({
          locationId,
          year: selectedYear
        });

        const fetchedScenarioId = scenarioResponse.data.id;
        setScenarioId(fetchedScenarioId);
        scenarioYearRef.current = scenarioResponse.data.year;

        if (fetchedScenarioId) {
          const patientApi = new PatientControllerApi(config);
          const [yearPlanResponse, patientsResponse, roomProps] = await Promise.all([
            yearPlanApi.getYearPlan({
              locationId,
              scenarioId: fetchedScenarioId
            }),
            patientApi.listPatients({ locationId }),
            fetchRoomProperties()
          ]);
          setYearPlanFromServer(yearPlanResponse.data);
          dispatch(setPatients(patientsResponse.data));
          dispatch(setRoomProperties(roomProps));
        } else {
          setYearPlanFromServer(null);
          dispatch(clearPatients());
          dispatch(clearRoomProperties());
        }
        setTreatmentPlanTemplates([]);
      } catch (error) {
        console.error('Failed to load year plan on year change:', error);
        dispatch(showError({ message: t('fetch-error') }));
        writeYearToUrl(scenarioYearRef.current, { replace: true });
      } finally {
        setLoading(false);
      }
    };
    fetchYearPlan();
  }, [locationId, selectedYear, setYearPlanFromServer, writeYearToUrl, dispatch, t]);

  // Add one or more treatment plans from a template as a batch
  const handleAddTreatmentPlan = useCallback(
    (
      template: TreatmentPlanTemplateResponse,
      startWeekNumber?: number,
      capacity?: number,
      frequency?: number,
      occurrences?: number,
      endWeekNumber?: number
    ) => {
      if (!yearPlan || !template.id) return;

      const templateWeekNumbers = template.weeks.map((w) => w.weekNumber);
      const minWeek =
        templateWeekNumbers.length > 0 ? Math.min(...templateWeekNumbers) : 0;
      const maxWeek =
        templateWeekNumbers.length > 0 ? Math.max(...templateWeekNumbers) : 0;
      const span = maxWeek - minWeek;

      const count = occurrences ?? 1;
      const gap = frequency ?? 0;
      const endLimit = endWeekNumber ?? Infinity;
      let currentStart = startWeekNumber ?? 0;

      const newPlans: TreatmentPlanDto[] = [];

      for (let i = 0; i < count; i++) {
        if (currentStart > endLimit) break;

        const weeks: TreatmentWeekDto[] =
          startWeekNumber != null
            ? buildWeeksFromTemplate(template, currentStart - minWeek, capacity)
            : [];

        newPlans.push({
          id: crypto.randomUUID(),
          treatmentPlanTemplateId: template.id,
          name: template.name,
          strokeColor: template.strokeColor,
          fillColor: template.fillColor,
          highlightColor: template.highlightColor,
          weeks
        });

        const endOfPlan = currentStart + span;
        currentStart = endOfPlan + gap + 1;
      }

      applyYearPlanEdit({
        ...yearPlan,
        treatmentPlans: [...yearPlan.treatmentPlans, ...newPlans]
      });
    },
    [yearPlan, applyYearPlanEdit]
  );

  // Save pre-move state so we can restore on discard
  const handleMoveStart = useCallback(() => {
    yearPlanSnapshot.current = yearPlan;
  }, [yearPlan]);

  // Restore pre-move state without affecting unsaved changes state
  const handleMoveDiscard = useCallback(() => {
    if (yearPlanSnapshot.current) {
      setYearPlanLocal(yearPlanSnapshot.current);
      yearPlanSnapshot.current = null;
    }
  }, []);

  // Confirm the move — now marks the change as a committed edit
  const handleMoveAccept = useCallback(() => {
    if (yearPlanSnapshot.current) {
      applyYearPlanEdit(yearPlan);
      yearPlanSnapshot.current = null;
    }
  }, [yearPlan, applyYearPlanEdit]);

  // Refetch year plan from server
  const refreshYearPlan = useCallback(async (): Promise<string | undefined> => {
    if (!locationId) return;
    const config = createApiConfiguration();
    const scenarioApi = new ScenarioControllerApi(config);
    const yearPlanApi = new YearPlanControllerApi(config);

    const scenarioResponse = await scenarioApi.getScenarioForYear({
      locationId,
      year: selectedYear
    });
    const fetchedScenarioId = scenarioResponse.data.id;
    if (fetchedScenarioId) {
      const patientApi = new PatientControllerApi(config);
      const [yearPlanResponse, patientsResponse] = await Promise.all([
        yearPlanApi.getYearPlan({
          locationId,
          scenarioId: fetchedScenarioId
        }),
        patientApi.listPatients({ locationId })
      ]);
      setYearPlanFromServer(yearPlanResponse.data);
      dispatch(setPatients(patientsResponse.data));
    }
    return fetchedScenarioId;
  }, [locationId, selectedYear, setYearPlanFromServer, dispatch]);

  const fetchTemplates = useCallback(async () => {
    const config = createApiConfiguration();
    const templateApi = new TreatmentPlanTemplateControllerApi(config);
    const response = await templateApi.getTreatmentPlanTemplates();
    setTreatmentPlanTemplates(response.data);
  }, []);

  const clearTemplates = useCallback(() => {
    setTreatmentPlanTemplates([]);
  }, []);

  // Discard all edits: refetch from server, resetting local state
  const handleDiscardAll = useCallback(async () => {
    await refreshYearPlan();
    yearPlanSnapshot.current = null;
  }, [refreshYearPlan]);

  // Save all edits to server, then sync local state with server response
  const handleAcceptAll = useCallback(async () => {
    if (!locationId || !yearPlan?.treatmentPlans) return;
    const config = createApiConfiguration();
    const scenarioApi = new ScenarioControllerApi(config);
    const yearPlanApi = new YearPlanControllerApi(config);

    const scenarioResponse = await scenarioApi.getScenarioForYear({
      locationId,
      year: selectedYear
    });
    const scenarioId = scenarioResponse.data.id;
    if (scenarioId) {
      const response = await yearPlanApi.updateTreatmentPlans({
        locationId,
        scenarioId,
        updateTreatmentPlansRequest: {
          treatmentPlans: yearPlan.treatmentPlans
        }
      });
      setYearPlanFromServer(response.data);
    }
    yearPlanSnapshot.current = null;
  }, [locationId, selectedYear, yearPlan, setYearPlanFromServer]);

  // Remove a treatment plan from local state (persisted to server on handleAcceptAll)
  const handleDeleteTreatmentPlan = useCallback(
    (treatmentPlanId: string) => {
      if (!yearPlan?.treatmentPlans) return;
      applyYearPlanEdit({
        ...yearPlan,
        treatmentPlans: yearPlan.treatmentPlans.filter((p) => p.id !== treatmentPlanId)
      });
    },
    [yearPlan, applyYearPlanEdit]
  );

  // Update capacity on the specified intervention groups for a plan (persisted to server on handleAcceptAll)
  const updateCapacity = useCallback(
    (treatmentPlanId: string, capacity: number, targetGroupIds: string[]) => {
      if (!yearPlan?.treatmentPlans || targetGroupIds.length === 0) return;
      const targetIdSet = new Set(targetGroupIds);
      applyYearPlanEdit({
        ...yearPlan,
        treatmentPlans: yearPlan.treatmentPlans.map((p) =>
          p.id === treatmentPlanId ? applyCapacityToPlan(p, capacity, targetIdSet) : p
        )
      });
    },
    [yearPlan, applyYearPlanEdit]
  );

  // Preview a move locally — uses setYearPlanLocal so unsaved changes state is not updated
  // until the move is confirmed via handleMoveAccept
  const handleMoveIntervention = useCallback(
    (params: MoveHoverParams) => {
      if (!yearPlan?.treatmentPlans) return;

      const delta = params.direction === 'left' ? -1 : 1;
      const planIndex = yearPlan.treatmentPlans.findIndex((p) => p.id === params.treatmentPlanId);
      if (planIndex === -1) return;

      const plan = yearPlan.treatmentPlans[planIndex];

      // Build a flat view of weeks containing groups
      type WeekRef = { weekIndex: number; weekNumber: number; groupIds: string[] };
      const weekRefs: WeekRef[] = plan.weeks.map((w, wi) => ({
        weekIndex: wi,
        weekNumber: w.weekNumber ?? 0,
        groupIds: (w.groups ?? []).map((g) => g.id ?? '').filter(Boolean)
      }));

      // Find the week containing the target group
      const targetWeekRef = weekRefs.find((wr) => wr.groupIds.includes(params.groupId));
      if (!targetWeekRef) return;

      const weeksToMove =
        params.scope === 'single'
          ? [targetWeekRef]
          : weekRefs.filter((wr) =>
              params.direction === 'left'
                ? wr.weekNumber <= params.weekNumber
                : wr.weekNumber >= params.weekNumber
            );

      const allValid = weeksToMove.every((wr) => {
        const newWeek = wr.weekNumber + delta;
        return newWeek >= 1 && newWeek <= totalWeeks;
      });
      if (!allValid) return;

      const weekIndicesToShift = new Set(weeksToMove.map((wr) => wr.weekIndex));
      const updatedWeeks = plan.weeks.map((w, wi) =>
        weekIndicesToShift.has(wi)
          ? { ...w, weekNumber: (w.weekNumber ?? 0) + delta }
          : w
      );

      const updatedPlans = [...yearPlan.treatmentPlans];
      updatedPlans[planIndex] = { ...plan, weeks: updatedWeeks };

      setYearPlanLocal({ ...yearPlan, treatmentPlans: updatedPlans });
    },
    [yearPlan, totalWeeks]
  );

  // Computes a summary of changes between local state and server snapshot.
  // Safe to read serverYearPlan.current here because it only changes via
  // setYearPlanFromServer, which also updates yearPlan (the memo dependency).
  const changeSummary = useMemo((): ChangeSummary | null => {
    const server = serverYearPlan.current;
    if (!yearPlan || !server) return null;

    const currentPlans = yearPlan.treatmentPlans;
    const serverPlans = server.treatmentPlans;

    const currentTotalCapacity = currentPlans.reduce((sum, p) => sum + aggregateCapacity(p), 0);
    const serverTotalCapacity = serverPlans.reduce((sum, p) => sum + aggregateCapacity(p), 0);

    const serverIds = new Set(serverPlans.map((p) => p.id));
    const currentIds = new Set(currentPlans.map((p) => p.id));

    const treatmentPlansAdded: Record<string, number> = {};
    for (const p of currentPlans.filter((p) => !serverIds.has(p.id))) {
      treatmentPlansAdded[p.name] = (treatmentPlansAdded[p.name] ?? 0) + 1;
    }

    const treatmentPlansDeleted: Record<string, number> = {};
    for (const p of serverPlans.filter((p) => !currentIds.has(p.id))) {
      treatmentPlansDeleted[p.name] = (treatmentPlansDeleted[p.name] ?? 0) + 1;
    }

    return {
      currentTotalCapacity,
      serverTotalCapacity,
      currentTotalPlans: currentPlans.length,
      serverTotalPlans: serverPlans.length,
      treatmentPlansAdded,
      treatmentPlansDeleted
    };
  }, [yearPlan]);

  return {
    yearPlan,
    changeSummary,
    treatmentPlanTemplates,
    selectedYear,
    setSelectedYear,
    scenarioId,
    locationName,
    loading,
    handleAddTreatmentPlan,
    handleDeleteTreatmentPlan,
    updateCapacity,
    handleMoveIntervention,
    handleMoveStart,
    handleMoveDiscard,
    handleMoveAccept,
    handleDiscardAll,
    handleAcceptAll,
    refreshYearPlan,
    fetchTemplates,
    clearTemplates
  };
}
